import { lazy, Suspense } from 'react'
// Route-level code splitting (2026-07-03): only the landing page and the
// tiny always-needed shell pieces load statically. Every other page is a
// lazy chunk — the previous single 7.9 MB (1.65 MB gzip) bundle carried
// three.js, tile-map/canvas-engine, hls.js and all pages to every visitor,
// dominating the cold start on Slow-4G (~13 s FCP, DevTools trace by Log).
import LandingPage from '@/components/LandingPage'
import SiteNav from '@/components/SiteNav'
import NavigationButton from '@/components/NavigationButton'
import { AuthProvider } from '@/auth'
import { I18nProvider } from '@/i18n'
import { ThemeProvider } from '@/theme'
import { CATALOG } from '@/data/catalog'
import { COVER_TO_KNOWLEDGE_ID } from '@/data/knowledgeIds'
import { getMapPath, getConfig, ProjectContext, injectTheme } from '@/config/project'
import { usePathname } from '@/lib/routing'
import { TschaukoAgentRoot } from '@/lib/voice-agent/TschaukoAgentRoot'

const SpeciesView = lazy(() => import('@/components/SpeciesView'))
const StationRedirect = lazy(() => import('@/components/StationRedirect'))
const PersonaPage = lazy(() => import('@/components/PersonaPage'))
const BrowsePage = lazy(() => import('@/components/BrowsePage'))
const RoutesPage = lazy(() => import('@/components/RoutesPage'))
const StoriesPage = lazy(() => import('@/components/StoriesPage'))
const AlphaVideoTest = lazy(() => import('@/components/AlphaVideoTest'))
const Styleguide = lazy(() => import('@/components/Styleguide'))
const RouteStripPage = lazy(() => import('@/components/RouteStripPage'))
const DialogPreview = lazy(() => import('@/components/DialogPreview'))
const GoogleCallback = lazy(() => import('@/components/GoogleCallback'))
const LegalPage = lazy(() => import('@/components/LegalPage'))
const ArDevPage = lazy(() => import('@/components/ArDevPage'))
const ThreeDTestPage = lazy(() => import('@/components/ThreeDTestPage'))
const KartenTestPage = lazy(() => import('@/components/KartenTestPage'))
const ArtrackTestPage = lazy(() => import('@/components/ArtrackTestPage'))
const DevConfigPage = lazy(() => import('@/components/DevConfigPage'))
const AdventuresPage = lazy(() => import('@/components/AdventuresPage'))
const AdventurePage = lazy(() => import('@/components/AdventurePage'))
const EntdeckenPage = lazy(() => import('@/components/EntdeckenPage'))
const DiscoverPage = lazy(() => import('@/components/DiscoverPage'))
const AudioGuideTest = lazy(() => import('@/components/AudioGuideTest'))
const ImmersivePage = lazy(() => import('@/components/ImmersivePage'))
const InfiniteGuidePage = lazy(() => import('@/components/InfiniteGuidePage'))
const TscheppBotPage = lazy(() => import('@/components/TscheppBotPage'))
const SubmitPage = lazy(() => import('@/components/SubmitPage'))
const GuidePage = lazy(() => import('@/components/GuidePage'))
const LivePage = lazy(() => import('@/components/LivePage'))
const OwlKeyTest = lazy(() => import('@/components/OwlKeyTest'))
const ImageReviewPage = lazy(() => import('@/components/ImageReviewPage'))
const NavigatePage = lazy(() => import('@/components/NavigatePage'))
const NavigateEntryPage = lazy(() => import('@/components/NavigateEntryPage'))
const NavigationMap = lazy(() => import('@/components/NavigationMap'))
const ThreeDSlidePage = lazy(() => import('@/components/ThreeDSlidePage'))
const WallPage = lazy(() => import('@/components/WallPage'))
const WallRemotePage = lazy(() => import('@/components/WallRemotePage'))
import { DEBUG_TOOLS_ENABLED } from '@/components/FooterNav'
const MobileDebugPanel = lazy(() => import('@/components/MobileDebugPanel'))
const KartenLabor = lazy(() => import('@/components/KartenLabor'))
const KARTEN_LABOR = typeof window !== 'undefined'
  && (new URLSearchParams(window.location.search).has('labor') || (() => { try { return sessionStorage.getItem('kartenLabor') === '1' } catch { return false } })())

// Inject theme CSS variables on startup
const projectConfig = getConfig()
injectTheme(projectConfig)

export default function App() {
  return (
    <ProjectContext.Provider value={projectConfig}>
      <I18nProvider>
        <AuthProvider>
          <ThemeProvider>
            <TschaukoAgentRoot>
              {/* One boundary for all lazy route chunks — fallback stays
                  empty so the landing shell doesn't flash a spinner. */}
              <Suspense fallback={null}>
                <AppRoutes />
              </Suspense>
            </TschaukoAgentRoot>
          </ThemeProvider>
        </AuthProvider>
      </I18nProvider>
      {/* Debug-Konsole (Panel + Zahnrad-Launcher) nur im Dev-Build oder mit
          ?debug=1 — für Endnutzer unsichtbar (Alex 27.07., Shot 115937; das
          Zahnrad kam sonst über ein hasFooterNav-Mount-Race zurück). */}
      {window.location.pathname !== '/tschepp-bot' && DEBUG_TOOLS_ENABLED && (
        <Suspense fallback={null}>
          <MobileDebugPanel />
        </Suspense>
      )}
      {/* Karten-Labor (?labor): Ansichten, FPS, Kachelfarbe — Testhost-Werkzeug */}
      {KARTEN_LABOR && (
        <Suspense fallback={null}>
          <KartenLabor />
        </Suspense>
      )}
    </ProjectContext.Provider>
  )
}

function AppRoutes() {
  const path = usePathname()
  const config = projectConfig

  // Navigation: Mapbox variant (only if feature enabled)
  if (path === '/navigate-mapbox' && config.features.navigation) return (
    <Suspense fallback={null}>
      <NavigationMap />
    </Suspense>
  )

  // Navigation: TileMap variant. Seit 2026-08-10 traegt der map-Modus den
  // Onboarding-Gate selbst (frische Tages-Auswahl -> Karte, sonst Flow).
  //
  // Die WURZEL ist die Karte (Alex 2026-08-10: "der Main-Slug ist eigentlich
  // Karte-Teil" — der geteilte Link tschepp.arkserver.arkturian.com muss
  // direkt in die App fuehren, nicht auf eine Landing). Die alte Landing
  // bleibt unter /landing als eigenstaendige Seite erreichbar.
  if ((path === '/' || path === '/navigate-tile') && config.features.navigation) return (
    <Suspense fallback={null}>
      <NavigateEntryPage mode="map" />
    </Suspense>
  )

  // Onboarding-Einstieg (Alex 2026-08-03): zeigt den Flow IMMER und mündet
  // danach in dieselbe Karte. Wiederholbarkeit hängt an der URL, nicht an
  // einem localStorage-Häkchen.
  if (path === '/start' && config.features.navigation) return (
    <Suspense fallback={null}>
      <NavigateEntryPage mode="onboarding" />
    </Suspense>
  )

  // Wandbildschirm am Empfangshaus (Prototyp, Post #5498): reine
  // Darstellung, animiert Weg 1–3 und den Weg zur Kassa selbst durch.
  if (path === '/wand' && config.features.navigation) return (
    <Suspense fallback={null}>
      <WallPage />
    </Suspense>
  )
  // Bildschirm-Variante ohne Bedienelemente + Steuerung vom Tablet
  // (über das Wand-Relais, server/wall-relay-server.mjs).
  if (path === '/wand/screen' && config.features.navigation) return (
    <Suspense fallback={null}>
      <WallPage variant="screen" />
    </Suspense>
  )
  if (path === '/wand/steuerung' && config.features.navigation) return (
    <Suspense fallback={null}>
      <WallRemotePage />
    </Suspense>
  )

  // 3D map — the SAME NavigatePage with the presenter renderer behind it
  // (Issue #313: one page, all overlays, two map worlds). /navigate-tile
  // stays the untouched default.
  if (path === '/navigate-3d' && config.features.navigation) return (
    <Suspense fallback={null}>
      <NavigatePage renderer="presenter3d" />
    </Suspense>
  )

  // Generische Terrain-3D-Welt (Post 4704) — DERSELBE NavigatePage-Slot,
  // dritter Renderer. /karten-test stellt alle drei nebeneinander.
  if (path === '/navigate-terrain' && config.features.navigation) return (
    <Suspense fallback={null}>
      <NavigatePage renderer="geo3d" />
    </Suspense>
  )
  if (path === '/karten-test' && config.features.navigation) return (
    <Suspense fallback={null}>
      <KartenTestPage />
    </Suspense>
  )

  // Entdecken — redesigned discover surface (mockups 114550/114551). The
  // classic list page is preserved at /entdecken-alt (NOT deleted — Alex
  // 2026-07-17), and the map's drop-up overlay keeps using EntdeckenContent.
  if (path === '/entdecken' && config.features.navigation) return (
    <Suspense fallback={null}>
      <DiscoverPage />
    </Suspense>
  )
  if (path === '/entdecken-alt' && config.features.navigation) return <EntdeckenPage />

  // Profil ist ein Drop-up ueber der Karte, keine eigene Seite (Alex
  // 2026-08-05: die eigenstaendige Seite war „enttaeuschend" — sie riss den
  // Besucher aus der Karte und sah aus wie ein Formular). Der Direktlink
  // bleibt gueltig und fuehrt dorthin, wo das Profil lebt.
  if (path === '/profil') {
    window.location.replace(`${getMapPath()}?profil=1`)
    return null
  }

  // Pages without global nav (always available)
  if (path === '/auth/google/callback') return <GoogleCallback />
  if (path === '/alpha-test') return <AlphaVideoTest />
  if (path === '/owl-test') return <OwlKeyTest />
  // Interne Bild-/3D-Triage (Post 4269) — unverlinkt, nur per Direkt-URL
  if (path === '/review-bilder') return <ImageReviewPage />
  if (path === '/styleguide') return <Styleguide />
  // Die ganze Route als senkrechter Strang mit allen Texten (Alex 2026-08-10).
  // Zweck: den Erzaehlfluss am Stueck lesen — Reihenfolge, Dichte,
  // Wiederholungen —, was die Karte nicht zeigt und das Aufklappen einzelner
  // Wegpunkte zu muehsam macht. Unverlinkt, nur per Direkt-URL.
  if (path === '/strecke') return <RouteStripPage />
  // Experimentelle, unverlinkte Knowledge-Galerie: eine gemeinsame WebGL-
  // Szene, vertikal per Kamera geblättert. Die produktiven Karten-/Detail-
  // Wege bleiben davon vollständig getrennt (Issue #1126).
  if (path === '/3dslide') return <ThreeDSlidePage />
  if (path === '/dialogs') return <DialogPreview />
  if (path === '/ardev') return <ArDevPage />
  if (path === '/artrack-test') return <ArtrackTestPage />
  if (path === '/adventure') return <AdventurePage />
  if (path === '/adventures') return <AdventuresPage />
  if (path === '/audio-guide-test') return <AudioGuideTest />
  if (path === '/immersive') return <ImmersivePage />
  if (path === '/infinite-guide') return <InfiniteGuidePage />
  if (path === '/tschepp-bot') return <TscheppBotPage />
  if (path === '/dev-config') return <DevConfigPage />
  // Standalone fullscreen Dr.-Tschauko chat. Same voice-agent session
  // as the drop-up overlay uses (App-level singleton), just rendered
  // fullscreen with a hero-portrait CTA that morphs into the compact
  // layout when the user taps to start (Alex 2026-07-01).
  if (path === '/chat') return <GuidePage />
  // Immersive "live view" variant of the Dr.-Tschauko chat: fullscreen
  // character video + live subtitles + one big push-to-talk. Same session
  // singleton as /chat (Alex 2026-07-12). /chat2 is an alias.
  if (path === '/live' || path === '/chat2') return <LivePage />
  // Weitere Figuren derselben Realtime-Oberflaeche (Alex 2026-08-03):
  // Route -> Persona-Kennung, mehr braucht eine neue Figur hier nicht.
  if (path === '/live-tschauka') return <LivePage personaId="tschauka" />
  if (path === '/live-tschauko') return <LivePage personaId="tschauko_owl" />

  // Feature-gated pages
  if (path === '/3dtest' && config.features.threeD) return <ThreeDTestPage />

  // Legal pages
  if (path === '/impressum') return <LegalPage type="impressum" />
  if (path === '/datenschutz') return <LegalPage type="datenschutz" />
  if (path === '/agb') return <LegalPage type="agb" />

  // Species detail — only if species feature enabled
  if (config.features.species) {
    const catalogMatch = path.match(/^\/(\d+)(?:\/(\d+))?$/)
    if (catalogMatch) {
      const catalogIndex = Number(catalogMatch[1])
      const species = CATALOG[catalogIndex - 1]
      if (species) {
        const annotationIndex = catalogMatch[2] ? Number(catalogMatch[2]) : undefined
        // Detail über knowledge_id öffnen, NICHT (nur) über storageId (Issue #442).
        //
        // Der Dual-Store liegt pro Art unterschiedlich herum: bei manchen Arten
        // sitzt der kanonische Annotations-Record auf dem Original-Cover, bei
        // anderen auf dem 4K-Bild (Ochsenfrosch: kid 245 auf source 112124; das
        // Original 101571 trägt nur einen alten Waisen-Record 101795). Karte UND
        // Annotationsmanager (knowledge.arkturian.com/arten) arbeiten über
        // knowledge_id und lesen/schreiben denselben Record. Öffnete die
        // Standalone-Seite nur über storageId → POST /annotated {storage_id},
        // landete sie bei diesen Arten auf dem Waisen-Record → Anker-Edits kamen
        // nicht an (Standalone ≠ Karte). Es gibt KEINE storageId-Wahl, die für
        // alle Arten stimmt — nur die knowledge_id ist stabil.
        //
        // storageId + model3dSourceId bleiben (Engagement-Keying + 3D-Quelle
        // unverändert); resolveAnnotatedKnowledge bevorzugt knowledge_id für den
        // Record, das Bild bleibt über mainIllustrationId (media[display]) das
        // Cover. Arten ohne Mapping (Aga-Kröte etc.) → knowledgeId undefined →
        // storageId-Pfad wie bisher.
        const knowledgeId = COVER_TO_KNOWLEDGE_ID[species.storageId]
        return <SpeciesView knowledgeId={knowledgeId} storageId={species.storageId} model3dSourceId={species.storageId} autoPlayAnnotation={annotationIndex} binom={species.latin} commonName={species.name} />
      }
    }
    /* Stelen-QR: /st/01 … /st/10 (Alex 2026-08-18). Bewusst NICHT /s/ —
       das Präfix darunter ist seit je die Storage-SpeciesView, und /s/01
       wäre still als Storage-Objekt 1 aufgelöst worden statt zu scheitern. */
    const stationMatch = path.match(/^\/st\/(\d{1,3})$/)
    if (stationMatch) return (
      <Suspense fallback={null}><StationRedirect nummer={stationMatch[1]} /></Suspense>
    )
    const storageMatch = path.match(/^\/s\/(\d+)$/)
    if (storageMatch) return <SpeciesView storageId={Number(storageMatch[1])} />
    const knowledgeMatch = path.match(/^\/k\/(\d+)$/)
    if (knowledgeMatch) return <SpeciesView knowledgeId={Number(knowledgeMatch[1])} />
    // Character-persona pages (Dr. Tschauko / Tschauko / Tschauka) — sourced
    // from Content-API posts (doc_type=persona). Draft-status until Alex
    // decides to publish; the URL still resolves for internal review.
    const personaMatch = path.match(/^\/persona\/([\w-]+)$/)
    if (personaMatch) return <PersonaPage slug={personaMatch[1]} />
  }

  // All pages with global nav
  const showNavButton = path === '/landing' || path === '/browse'
  return (
    <>
      <SiteNav />
      <PageContent path={path} config={config} />
      {showNavButton && config.features.navigation && <NavigationButton />}
    </>
  )
}

function PageContent({ path, config }: { path: string; config: typeof projectConfig }) {
  if (path === '/landing') return <LandingPage />
  if (path === '/routes') return <RoutesPage />
  if (path === '/submit') return <SubmitPage />

  // Feature-gated content pages
  if (path === '/browse' && config.features.species) return <BrowsePage />
  if (path === '/stories' && config.features.stories) return <StoriesPage />

  if (config.features.stories) {
    const storyNumMatch = path.match(/^\/story\/(\d+)$/)
    if (storyNumMatch) return <StoriesPage storyIndex={Number(storyNumMatch[1])} />

    const storyMatch = path.match(/^\/stories\/(.+)$/)
    if (storyMatch) return <StoriesPage storyId={storyMatch[1]} />
  }

  // Fallback
  return <LandingPage />
}
